// @flow

import React from 'react';
import map from 'lodash/map';
import find from 'lodash/find';
import flatMap from 'lodash/flatMap';
import flow from 'lodash/fp/flow';
import filter from 'lodash/fp/filter';
import propEq from 'lodash/fp/propEq';
import fpMap from 'lodash/fp/map';
import type {
  Route, Group, L10n, Person, Match,
} from 'type/type';
import { withRouter } from 'react-router-dom';
import Spinner from '@active/awe-ui-core/lib/components/Spinner';
import NoRecord from 'components/NoRecord/NoRecord';
import connect from '@active/awe-ui-core/lib/hoc/connect';
import injectL10n from 'react-aaui/lib/shared/injectL10n';
import { isKioskMode } from 'services/kiosk/kiosk';

import {
  getRoutes,
  getGroupsByRoutes,
  getResultRouteId,
  getResultDivisionId,
  getEventViewType,
} from '../../duck/selector';
import {
  fetchLeaderBoardResult as fetchLeaderBoardResultAction,
  clearLeaderBoardResult as clearLeaderBoardResultAction,
  changeLeaderBoardCourseId as changeLeaderBoardCourseIdAction,
  changeLeaderBoardDivisionId as changeLeaderBoardDivisionIdAction,
} from '../../duck/duck';
import LeaderBoardItem from '../LeaderBoardItem/LeaderBoardItem';
import FlexibleDropdown from '../FlexibleDropDown/FlexibleDropdown';

import '../../duck/epics';
import '../Home.scss';

type param = {
  value: string
}

type resultType = {
  id: string,
  bib: string,
  person: Person,
  splitResults: []
}

type Props = {
  groups: Group[],
  routes: Route[],
  courseId: string,
  divisionId: string,
  loadingEventResult: boolean,
  match: Match,
  resultList: resultType[],
  l10n: L10n,
  changeLeaderBoardCourseId: Function,
  changeLeaderBoardDivisionId: (id: string) => void,
  fetchLeaderBoardResult: Function,
  clearLeaderBoardResult: Function,
  viewType: 'VIRTUAL' | 'PHYSICAL',
}

const INTERVAL = 30000;

export class LeaderBoard extends React.Component<Props> {
  static defaultProps = {
    groups: [],
    routes: [],
    courseId: '',
    divisionId: '',
    resultList: [],
  }

  timer: TimeoutID

  componentDidMount() {
    this.createTimer(false);
  }

  componentWillUnmount() {
    this.timer && clearTimeout(this.timer);
  }

  createTimer = (hideLoading: boolean) => {
    const {
      courseId,
      divisionId,
      match: { params: { applicationName } },
      fetchLeaderBoardResult,
    } = this.props;

    if (courseId && divisionId) {
      fetchLeaderBoardResult({
        courseId, divisionId, applicationName, hideLoading,
      });
    }

    this.timer = setTimeout(() => {
      this.createTimer(true);
    }, INTERVAL);
  }

  changeLeaderBoardCourseId = ({ value }: param) => {
    const {
      changeLeaderBoardCourseId,
      routes,
    } = this.props;
    const { groups } = find(routes, route => route.id === value);
    const groupId = groups[0] && groups[0].id;

    changeLeaderBoardCourseId({ courseId: value, divisionId: groupId });
  }

  changeLeaderBoardDivisionId = ({ value }: param) => {
    const {
      changeLeaderBoardDivisionId,
    } = this.props;

    changeLeaderBoardDivisionId(value);
  }

  render() {
    const {
      groups,
      routes,
      courseId,
      divisionId,
      resultList,
      loadingEventResult,
      l10n,
      viewType,
    } = this.props;

    const isKiosk = isKioskMode(window);
    const { search } = window.location;

    const groupsData = map(groups, group => ({
      text: group.name,
      value: group.id,
    }));
    const routesData = flow(
      filter(propEq('virtual', viewType === 'VIRTUAL')),
      fpMap(route => ({
        text: route.name,
        value: route.id,
      })),
    )(routes);

    const currentRoute = find(routes, route => route.id === courseId);
    const stages = flatMap(currentRoute.stages, stageResult => stageResult && stageResult.routeStagePoints);

    const dropdownItems = [
      {
        itemClasses: 'event-home__filter',
        label: l10n.formatMessage('page.event.course'),
        data: routesData,
        value: courseId.toString(),
        onChange: this.changeLeaderBoardCourseId,
      },
      {
        itemClasses: 'event-home__filter',
        label: l10n.formatMessage('page.event.division'),
        data: groupsData,
        value: divisionId.toString(),
        onChange: this.changeLeaderBoardDivisionId,
      },
    ];

    const resultListComponent = resultList.length === 0 ? <NoRecord /> : map(resultList, (item, index) => {
      return (
        <LeaderBoardItem
          stages={stages}
          item={item}
          key={item.id}
          index={index}
          l10n={l10n}
          isKioskMode={isKiosk}
          queryString={search} />
      );
    });

    return (
      <div className="event-home__content">
        <div className="event-home__header">
          <FlexibleDropdown dropdownItems={dropdownItems} />
        </div>
        {loadingEventResult ? <Spinner /> : resultListComponent}
      </div>
    );
  }
}

const mapState = (state) => {
  return ({
    viewType: getEventViewType(state),
    groups: getGroupsByRoutes(state),
    routes: getRoutes(state),
    courseId: getResultRouteId(state),
    divisionId: getResultDivisionId(state),
    loadingEventResult: state.getIn([ 'eventPage', 'loadingEventResult' ]),
    resultList: state.getIn([ 'eventPage', 'leaderBoardResult' ]),
  });
};

const LeaderBoardContainer = connect(mapState, {
  fetchLeaderBoardResult: fetchLeaderBoardResultAction,
  clearLeaderBoardResult: clearLeaderBoardResultAction,
  changeLeaderBoardCourseId: changeLeaderBoardCourseIdAction,
  changeLeaderBoardDivisionId: changeLeaderBoardDivisionIdAction,
})(withRouter(injectL10n()(LeaderBoard)));

export default LeaderBoardContainer;
